import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { isAuthenticated, loginWithPassword, logout, getSession } from '../auth/session';
import { loginWithSso } from '../auth/dialog';
import { checkEmailAccount } from '../api/auth';
import { uploadDocumentToDeal, uploadDocument, preSignedFileUpload, setContractToImported } from '../api/contracts';
import { listAttachments, getAttachmentBytes, getItemMeta } from '../office/mailbox';
import { getUploadsForCurrentItem, recordUpload, clearUploadsForCurrentItem } from '../office/uploadHistory';
import { DealCombobox } from './DealCombobox';
export const App = () => {
    const [view, setView] = useState(isAuthenticated() ? 'main' : 'login');
    return view === 'login' ? (_jsx(LoginView, { onAuthed: () => setView('main') })) : (_jsx(MainView, { onLogout: () => { logout(); setView('login'); } }));
};
const LoginView = ({ onAuthed }) => {
    const [step, setStep] = useState({ kind: 'email' });
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const handleEmailContinue = async (email) => {
        const normalized = email.trim().toLowerCase();
        if (!normalized)
            return;
        setBusy(true);
        setError(null);
        try {
            const result = await checkEmailAccount(normalized);
            await routeByType(result, normalized);
        }
        catch (err) {
            setError(err.message);
        }
        finally {
            setBusy(false);
        }
    };
    // Mirrors dealroom-fe EmailForm/LoginForm routing: SSO/Google/Microsoft are
    // handled explicitly, ACCOUNT_NOT_ACTIVATED without an org is "under review",
    // everything else falls through to the password form.
    const routeByType = async (result, email) => {
        switch (result.type) {
            case 'GOOGLE_LOGIN':
                await loginWithSso({ flow: 'cognito', provider: 'Google', loginHint: email });
                onAuthed();
                return;
            case 'MICROSOFT_LOGIN':
                await loginWithSso({ flow: 'cognito', provider: 'MicrosoftAzure', loginHint: email });
                onAuthed();
                return;
            case 'SSO_LOGIN':
                // top.legal SSO — the backend owns the SAML round trip, we only need the org it is for.
                if (!result.organisationID) {
                    setError('Your organisation is not fully set up for SSO. Please contact your top.legal admin.');
                    return;
                }
                await loginWithSso({ flow: 'saml', organisationID: result.organisationID });
                onAuthed();
                return;
            case 'EMAIL_ADDRESS_NOT_FOUND':
                setError('Please verify your email address. Your account has not been found.');
                return;
            case 'ACCOUNT_NOT_ACTIVATED':
                if (!result.organisationID) {
                    setError("Your access request is under review. You'll be notified once your account is approved.");
                    return;
                }
                setStep({ kind: 'password', email });
                return;
            case 'NO_ACCESS':
                setError("You don't have access. Contact your top.legal admin.");
                return;
            case 'DEAL_DELETED':
                setError('That deal no longer exists.');
                return;
            // EMAIL_LOGIN, MAGIC_LINK_SENT, and any other type → password form.
            default:
                setStep({ kind: 'password', email });
        }
    };
    const handlePassword = async (email, password) => {
        setBusy(true);
        setError(null);
        try {
            await loginWithPassword(email, password);
            onAuthed();
        }
        catch (err) {
            setError(err.message);
        }
        finally {
            setBusy(false);
        }
    };
    return (_jsxs("div", { children: [_jsx("h2", { children: "Send to dealroom" }), _jsx("p", { className: "muted", style: { marginTop: 4, marginBottom: 12 }, children: "Upload PDF email attachments \u2014 contracts, NDAs, amendments \u2014 directly into a top.legal dealroom from Outlook. No more downloading and re-uploading: pick an email, choose a deal, and the PDFs arrive in top.legal ready for review, redlining, and signing." }), _jsxs("ul", { className: "muted", style: { marginTop: 0, marginBottom: 16, paddingLeft: 18, fontSize: 12 }, children: [_jsx("li", { children: "Send PDF attachments to a specific dealroom, or let AI assign one automatically." }), _jsx("li", { children: "Keeps a per-email history so you don't upload the same file twice." }), _jsx("li", { children: "Only PDF files are supported. Requires a top.legal account." })] }), _jsx("h3", { style: { marginBottom: 8 }, children: "Sign in to top.legal" }), step.kind === 'email' && (_jsx(EmailStep, { busy: busy, onContinue: handleEmailContinue })), step.kind === 'password' && (_jsx(PasswordStep, { email: step.email, busy: busy, onSubmit: (pw) => handlePassword(step.email, pw), onBack: () => { setError(null); setStep({ kind: 'email' }); } })), error && _jsx("div", { className: "error", children: error })] }));
};
const SIGN_UP_URL = 'https://app.top.legal/auth/register';
const LEARN_MORE_URL = 'https://top.legal';
const EmailStep = ({ busy, onContinue }) => {
    const [email, setEmail] = useState('');
    return (_jsxs("form", { onSubmit: (e) => { e.preventDefault(); onContinue(email); }, children: [_jsx("label", { children: "Email" }), _jsx("input", { type: "email", value: email, onChange: (e) => setEmail(e.target.value), required: true, autoFocus: true, placeholder: "you@company.com" }), _jsx("div", { style: { marginTop: 12 }, children: _jsx("button", { type: "submit", className: "primary", disabled: busy || !email, children: busy ? 'Checking…' : 'Continue' }) }), _jsxs("div", { className: "muted", style: { marginTop: 16, fontSize: 12 }, children: ["Don't have a top.legal account yet?", ' ', _jsx("a", { href: SIGN_UP_URL, target: "_blank", rel: "noopener noreferrer", children: "Sign up" }), ' · ', _jsx("a", { href: LEARN_MORE_URL, target: "_blank", rel: "noopener noreferrer", children: "Learn more" })] })] }));
};
const PasswordStep = ({ email, busy, onSubmit, onBack }) => {
    const [password, setPassword] = useState('');
    return (_jsxs("form", { onSubmit: (e) => { e.preventDefault(); onSubmit(password); }, children: [_jsx("label", { children: "Email" }), _jsx("input", { type: "email", value: email, disabled: true }), _jsx("label", { children: "Password" }), _jsx("input", { type: "password", value: password, onChange: (e) => setPassword(e.target.value), required: true, autoFocus: true }), _jsxs("div", { style: { marginTop: 12 }, className: "row", children: [_jsx("button", { type: "submit", className: "primary", disabled: busy || !password, children: busy ? 'Signing in…' : 'Sign in' }), _jsx("button", { type: "button", onClick: onBack, disabled: busy, children: "Use a different email" })] }), _jsxs("div", { className: "muted", style: { marginTop: 16, fontSize: 12 }, children: ["Don't have a top.legal account yet?", ' ', _jsx("a", { href: SIGN_UP_URL, target: "_blank", rel: "noopener noreferrer", children: "Sign up" })] })] }));
};
const MainView = ({ onLogout }) => {
    const meta = getItemMeta();
    const [attachments, setAttachments] = useState([]);
    const [selected, setSelected] = useState(new Set());
    const [selectedDeal, setSelectedDeal] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [statuses, setStatuses] = useState({});
    const [history, setHistory] = useState({});
    const [lastResult, setLastResult] = useState(null);
    useEffect(() => {
        setAttachments(listAttachments());
        setHistory(getUploadsForCurrentItem());
    }, []);
    const toggle = (id) => {
        const next = new Set(selected);
        next.has(id) ? next.delete(id) : next.add(id);
        setSelected(next);
    };
    const handleUpload = async () => {
        const toUpload = Array.from(selected).filter((id) => !history[id]);
        if (toUpload.length === 0) {
            setError('Select at least one attachment that hasn\'t been uploaded yet');
            return;
        }
        setBusy(true);
        setError(null);
        setLastResult(null);
        const next = {};
        toUpload.forEach((id) => { next[id] = 'pending'; });
        setStatuses(next);
        let ok = 0;
        let failed = 0;
        const succeeded = new Set();
        for (const id of toUpload) {
            const att = attachments.find((a) => a.id === id);
            try {
                const bytes = await getAttachmentBytes(id);
                const { contract, preSignedUploadData } = selectedDeal
                    ? await uploadDocumentToDeal(selectedDeal.dealID, att.name)
                    : await uploadDocument(att.name);
                await preSignedFileUpload(preSignedUploadData, att.name, att.contentType, bytes);
                await setContractToImported(contract.contractID);
                const rec = {
                    contractID: contract.contractID,
                    contractName: att.name,
                    dealID: selectedDeal?.dealID,
                    dealName: selectedDeal?.name,
                    uploadedAt: Date.now(),
                };
                recordUpload(id, rec);
                setHistory((h) => ({ ...h, [id]: rec }));
                setStatuses((s) => ({ ...s, [id]: 'ok' }));
                succeeded.add(id);
                ok++;
            }
            catch (e) {
                setStatuses((s) => ({ ...s, [id]: e.message }));
                failed++;
            }
        }
        setBusy(false);
        setLastResult({ ok, failed });
        setSelected((prev) => {
            const next = new Set(prev);
            succeeded.forEach((id) => next.delete(id));
            return next;
        });
    };
    const session = getSession();
    return (_jsxs("div", { children: [_jsxs("div", { className: "row", style: { justifyContent: 'space-between' }, children: [_jsx("h2", { children: "Send to dealroom" }), _jsx("button", { onClick: onLogout, children: "Sign out" })] }), _jsx("div", { className: "muted", children: session?.email || session?.source }), _jsx("label", { children: "Email" }), _jsxs("div", { className: "muted", children: [_jsx("div", { children: _jsx("strong", { children: meta?.subject || '(no subject)' }) }), _jsxs("div", { children: [meta?.fromName, " <", meta?.from, ">"] })] }), _jsx("label", { children: "Attachments (PDF only)" }), attachments.length === 0 && (_jsx("div", { className: "muted", children: "No PDF attachments on this email. Only PDF files can be uploaded to top.legal." })), attachments.map((a) => {
                const past = history[a.id];
                const live = statuses[a.id];
                return (_jsxs("div", { className: "attach", children: [_jsx("input", { type: "checkbox", checked: selected.has(a.id), disabled: !!past, onChange: () => toggle(a.id) }), _jsxs("span", { className: "name", children: [a.name, past && (_jsxs("div", { className: "muted", style: { fontSize: 11 }, children: ["Uploaded", past.dealName ? ` to ${past.dealName}` : '', " \u00B7 ", new Date(past.uploadedAt).toLocaleString()] }))] }), _jsxs("span", { className: "size", children: [Math.round(a.size / 1024), " KB"] }), live === 'pending' && _jsx("span", { className: "status", children: "\u2026" }), (live === 'ok' || past) && _jsx("span", { className: "status ok", children: "\u2713" }), live && live !== 'pending' && live !== 'ok' && !past && (_jsx("span", { className: "status err", title: live, children: "\u2717" }))] }, a.id));
            }), _jsx("label", { children: "Dealroom (optional \u2014 leave empty to let AI assign)" }), _jsx(DealCombobox, { value: selectedDeal, onChange: setSelectedDeal }), _jsx("div", { style: { marginTop: 16 }, children: _jsx("button", { className: "primary", onClick: handleUpload, disabled: busy || selected.size === 0, children: busy
                        ? 'Uploading…'
                        : selectedDeal
                            ? `Upload to ${selectedDeal.name}`
                            : `Upload — AI will assign a dealroom` }) }), lastResult && (_jsxs("div", { style: {
                    marginTop: 12, padding: 8, borderRadius: 4,
                    background: lastResult.failed ? '#fef2f2' : '#f0fdf4',
                    color: lastResult.failed ? '#991b1b' : '#166534',
                    fontSize: 13,
                }, children: [lastResult.ok > 0 && _jsxs("div", { children: ["\u2713 ", lastResult.ok, " attachment", lastResult.ok === 1 ? '' : 's', " uploaded successfully."] }), lastResult.failed > 0 && _jsxs("div", { children: ["\u2717 ", lastResult.failed, " failed \u2014 see status icons above."] })] })), Object.keys(history).length > 0 && (_jsx("button", { style: { marginTop: 8, fontSize: 12 }, onClick: () => { clearUploadsForCurrentItem(); setHistory({}); setLastResult(null); setStatuses({}); }, children: "Reset upload history for this email" })), error && _jsx("div", { className: "error", children: error })] }));
};
